import { useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import {
  useCreateIncomeRecordMutation,
  useUpdateIncomeRecordMutation,
  useDeleteIncomeRecordMutation,
  useApproveIncomeRecordMutation,
} from "../api";
import type { IncomeRecordCreateRequest, IncomeRecordUpdateRequest } from "../types";
import type { PromptState } from "./useIncomeModals";

export interface UseIncomeMutationsReturn {
  creating: boolean;
  updatingId: number | null;
  deletingId: number | null;
  approvingId: number | null;
  isUpdating: boolean;
  isDeleting: boolean;
  isApproving: boolean;
  handleCreate: (dto: IncomeRecordCreateRequest) => Promise<void>;
  handleUpdate: (id: number, dto: IncomeRecordUpdateRequest) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  handleApprove: (id: number) => Promise<{ ok: boolean; conflict: boolean }>;
  setPrompt: (state: PromptState | null) => void;
}

export function useIncomeMutations(setPrompt: (state: PromptState | null) => void): UseIncomeMutationsReturn {
  const { t } = useTranslation("IncomePage");

  const [createIncomeRecord] = useCreateIncomeRecordMutation();
  const [updateIncomeRecord] = useUpdateIncomeRecordMutation();
  const [deleteIncomeRecord] = useDeleteIncomeRecordMutation();
  const [approveIncomeRecord] = useApproveIncomeRecordMutation();

  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);

  const isUpdating = updatingId !== null;
  const isDeleting = deletingId !== null;
  const isApproving = approvingId !== null;

  async function handleCreate(dto: IncomeRecordCreateRequest) {
    if (creating) return;
    try {
      setCreating(true);
      await createIncomeRecord(dto).unwrap();
      setPrompt({
        title: t("messages.createSuccessTitle"),
        text: t("messages.createSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.createErrorText", t),
        variant: "error",
      });
      throw err;
    } finally {
      setCreating(false);
    }
  }

  async function handleUpdate(id: number, dto: IncomeRecordUpdateRequest) {
    if (isUpdating) return;
    try {
      setUpdatingId(id);
      await updateIncomeRecord({ id, data: dto }).unwrap();
      setPrompt({
        title: t("messages.updateSuccessTitle"),
        text: t("messages.updateSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.updateErrorText", t),
        variant: "error",
      });
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleDelete(id: number) {
    if (isDeleting) return;
    try {
      setDeletingId(id);
      await deleteIncomeRecord(id).unwrap();
      setPrompt({
        title: t("messages.deleteSuccessTitle"),
        text: t("messages.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteErrorText", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  async function handleApprove(id: number): Promise<{ ok: boolean; conflict: boolean }> {
    if (isApproving) return { ok: false, conflict: false };
    try {
      setApprovingId(id);
      await approveIncomeRecord(id).unwrap();
      setPrompt({
        title: t("messages.confirmSuccessTitle"),
        text: t("messages.confirmSuccessText"),
        variant: "success",
      });
      return { ok: true, conflict: false };
    } catch (err: unknown) {
      const apiErr = err as { data?: { error?: string } };
      const isAlreadyConfirmed =
        typeof apiErr?.data?.error === "string" &&
        apiErr.data.error.includes("Утвердить можно только черновик");
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.confirmErrorText", t),
        variant: "error",
      });
      return { ok: false, conflict: isAlreadyConfirmed };
    } finally {
      setApprovingId(null);
    }
  }

  return {
    creating,
    updatingId,
    deletingId,
    approvingId,
    isUpdating,
    isDeleting,
    isApproving,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleApprove,
    setPrompt,
  };
}
